<template>
  <div ref="customerServiceRef" class="ai-customer-service" :class="{ 'ai-customer-service--open': isOpen }">
    <Transition name="ai-service-panel">
      <section v-if="isOpen" ref="servicePanelRef" class="service-panel" aria-label="AI 智能客服" @pointerdown.stop @wheel.stop>
        <div class="service-panel__topbar">
          <Transition name="ai-service-header" mode="out-in">
            <header v-if="isGuideMode && !isHistoryLoading" key="guide" class="guide-header">
              <div class="guide-profile">
                <img :src="guideAvatar" alt="小空 AI 智能助手" class="guide-avatar" />
                <div>
                  <img :src="guideTitle" alt="我是小空" class="guide-title" />
                  <span>您的AI智能助手</span>
                </div>
              </div>
              <img :src="closeIcon" alt="关闭 AI 客服" class="guide-close-icon" @click="close" />
            </header>

            <header v-else key="compact" class="service-panel__header">
              <img :src="compactAvatar" alt="小空 AI 智能助手" class="service-panel__avatar" />
              <h2>小空</h2>
              <img :src="closeIcon" alt="关闭 AI 客服" class="close-icon" @click="close" />
            </header>
          </Transition>
        </div>

        <div
          :ref="setMessageListRef"
          class="service-panel__content"
          :class="{ 'service-panel__content--guide': isGuideMode }"
          @scroll="handleContentScroll"
        >
          <template v-if="!isHistoryLoading && messages.length === 0">
            <p class="service-panel__prompt">有什么可以帮你?</p>
            <div class="quick-questions">
              <button
                v-for="question in quickQuestions"
                :key="question"
                class="quick-question"
                type="button"
                @click="sendMessage({ content: question })"
              >
                <img :src="questionIcon" alt="" />
                <span>{{ question }}</span>
              </button>
            </div>
          </template>

          <div v-else-if="!isHistoryLoading" class="message-list">
            <div
              v-for="message in messages"
              :key="message.id"
              class="message-row"
              :class="`message-row--${message.role}`"
            >
              <div v-if="message.role === 'user' && messageTimeLabels.has(message.id)" class="message-time">{{ formatMessageTime(message.timestamp!, messageTimeLabels.get(message.id) === 'datetime') }}</div>
              <template v-if="message.imageUrl">
                <img :src="message.imageUrl" alt="用户上传的图片" class="message-image" @click="previewImageUrl = message.imageUrl" />
              </template>
              <div v-if="message.content && message.role === 'assistant'" class="message-bubble" :class="{ 'message-bubble--error': message.isError }" @click="handleMarkdownLinkClick" v-html="renderCustomerServiceMarkdown(message.content, isReplying && message.id === messages.at(-1)?.id)"></div>
              <div v-else-if="message.content && message.role === 'user'" class="message-bubble">{{ message.content }}</div>
              <div v-if="message.role === 'user' && message.retryable" class="message-retry-area">
                <span class="message-retry-status">小空正在忙碌中，请稍等</span>
                <button class="message-retry-button" type="button" @click="retryMessage(message)">
                  <img :src="retryIcon" alt="" />
                  <span>重试</span>
                </button>
              </div>
              <div v-if="message.role === 'assistant' && message.isHumanService" class="human-service-card">
                <span>联系我们的客户经理：</span>
                <img :src="wechatQrCode" alt="微信扫码咨询" @click="previewImageUrl = wechatQrCode" />
                <span>微信扫码咨询</span>
              </div>
              <div v-if="message.role === 'assistant' && !message.isHumanService && message.id === latestFollowupMessageId && message.followupQuestions?.length" class="message-followups">
                <button
                  v-for="question in message.followupQuestions.slice(0, 3)"
                  :key="question"
                  type="button"
                  @click="sendMessage({ content: question })"
                >
                  {{ question }}
                </button>
              </div>
            </div>
            <div v-if="isThinking" class="thinking-indicator">
              <span v-if="!thinkingLabelVisible" class="thinking-dots" aria-hidden="true">
                <i></i>
                <i></i>
                <i></i>
              </span>
              <span v-if="thinkingLabelVisible" class="thinking-label">小空正在思考中...</span>
            </div>
          </div>
        </div>

        <div class="service-panel__composer-area">
          <div v-if="sensitiveWordError" class="sensitive-word-error"><img :src="baocuoIcon" alt="" /><span>当前内容暂无法处理，请调整后重试</span></div>
          <div v-if="showScrollBottom" class="scroll-bottom-slot">
            <button
              class="scroll-bottom-button"
              type="button"
              aria-label="回到底部"
              @click="scrollToBottom"
            >
              <img v-if="isReplying" :src="loadingRingIcon" alt="" class="scroll-bottom-spinner" />
              <img :src="scrollBottomIcon" alt="" class="scroll-bottom-icon" />
            </button>
          </div>

          <form
            class="message-composer"
            :class="{ 'message-composer--expanded': isComposerExpanded, 'message-composer--with-image': draftImageUrl }"
            @submit.prevent="submitDraft"
          >
          <input
            ref="fileInputRef"
            id="ai-customer-service-file-input"
            class="file-input"
            type="file"
            accept="image/*"
            aria-label="上传图片"
            @change="handleFileChange"
          />
          <label
            v-if="!draftImageUrl"
            class="composer-icon-button"
            for="ai-customer-service-file-input"
            aria-label="上传图片"
          >
            <img :src="addIcon" alt="" />
          </label>
          <div class="message-composer__field">
            <div v-if="draftImageUrl" class="draft-preview">
              <img :src="draftImageUrl" alt="待发送图片" class="draft-image" />
              <button class="draft-image__remove" type="button" aria-label="删除图片" @click="clearDraftImage">
                <img :src="draftImageCloseIcon" alt="" />
              </button>
            </div>
            <textarea
              ref="textareaRef"
              :class="{ 'message-composer__textarea--expanded': isComposerExpanded, 'message-composer__textarea--with-image': draftImageUrl }"
              v-model="draft"
              aria-label="输入咨询内容"
              placeholder="请输入内容"
              rows="1"
              maxlength="200"
              @input="resizeTextarea"
              @keydown.enter.exact="handleTextareaEnter"
            />
          </div>
          <button
            class="send-button"
            type="button"
            :aria-label="sendButtonLabel"
            :disabled="!canSend && !isReplying"
            @click="isReplying ? stopReply(true) : submitDraft()"
          >
            <img :src="sendButtonIcon" class="send-button__icon" :alt="sendButtonLabel" />
          </button>
          </form>
        </div>
      </section>
    </Transition>

    <button v-if="!isOpen" class="service-launcher" type="button" aria-label="打开 AI 客服" @click="open">
      <img :src="launcherAvatar" alt="" />
    </button>
    <image-preview-overlay :visible="Boolean(previewImageUrl)" :image-url="previewImageUrl" @close="previewImageUrl = ''" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, type ComponentPublicInstance } from 'vue'
import {
  aiBaseUrl,
  type AiCustomerChatHistoryItem,
  type AiCustomerChatRecord,
  type AiCustomerChatStreamEvent,
  type AiCustomerFaq,
  getAiCustomerChatRecords,
  getAiCustomerFaqs,
  recordAiCustomerFaq,
  stopAiCustomerChat,
  streamAiCustomerChat,
  uploadAiCustomerFile,
} from '@/api/ai'
import guideAvatar from '@/assets/images/1-ezgif.com-optimize.gif'
import compactAvatar from '@/assets/images/1-ezgif.com-optimize1.gif'
import launcherAvatar from '@/assets/images/aikefu.png'
import sendIcon from '@/assets/images/fasong.png'
import idleSendIcon from '@/assets/images/fasong2.png'
import scrollBottomIcon from '@/assets/images/huidaodibu .png'
import closeIcon from '@/assets/images/huisetu.png'
import draftImageCloseIcon from '@/assets/images/Closeone.png'
import loadingRingIcon from '@/assets/images/lansiquan.png'
import addIcon from '@/assets/images/tianjia.png'
import stopIcon from '@/assets/images/tingzhi.png'
import guideTitle from '@/assets/images/title111.png'
import wechatQrCode from '@/assets/images/xiaokong1.png'
import questionIcon from '@/assets/images/wenti1.png'
import { renderCustomerServiceMarkdown } from '@/utils/customer-service-markdown'
import baocuoIcon from '@/assets/images/baocuo22.png'
import retryIcon from '@/assets/images/chognshi22.png'
import ImagePreviewOverlay from './ImagePreviewOverlay.vue'

type MessageRole = 'assistant' | 'user'

interface SendMessagePayload {
  content?: string
  imageUrl?: string
  fileId?: string
  isHistory?: boolean
}

interface ChatMessage {
  id: number | string
  role: MessageRole
  timestamp?: number
  content?: string
  imageUrl?: string
  fileId?: string
  followupQuestions?: string[]
  isError?: boolean
  retryable?: boolean
  isHumanService?: boolean
}

const quickQuestions = ref<string[]>([])
const faqAnswers = ref(new Map<string, string>())
const conversationId = ref('')

const AI_CUSTOMER_SERVICE_LAST_OPENED_AT_KEY = 'ai-customer-service-last-opened-at'
const AI_CUSTOMER_SERVICE_CONVERSATION_ID_KEY = 'ai-customer-service-conversation-id'
const AI_CUSTOMER_SERVICE_GUIDE_INTERVAL = 48 * 60 * 60 * 1000

const isOpen = ref(false)
const isGuideMode = ref(true)
const isHistoryLoading = ref(false)
const isReplying = ref(false)
const isThinking = ref(false)
const isUploadingFile = ref(false)
const thinkingLabelVisible = ref(false)
const draft = ref('')
const draftImageUrl = ref('')
const previewImageUrl = ref('')
const sensitiveWordError = ref(false)
let sensitiveWordErrorTimer: ReturnType<typeof window.setTimeout> | undefined
const draftFileId = ref('')
const messages = ref<ChatMessage[]>([])
const isComposerExpanded = ref(false)
const showScrollBottom = ref(false)
const servicePanelRef = ref<HTMLElement>()
const customerServiceRef = ref<HTMLElement>()
const messageListRef = ref<HTMLElement>()
const fileInputRef = ref<HTMLInputElement>()
const textareaRef = ref<HTMLTextAreaElement>()
const imageObjectUrls = new Set<string>()
let faqReplyTimer: ReturnType<typeof window.setTimeout> | undefined
let thinkingLabelTimer: ReturnType<typeof window.setTimeout> | undefined
let scrollFrame: number | undefined
let autoScrollEnabled = true
let isProgrammaticScroll = false
let lastMessageListScrollTop = 0
let chatStreamController: AbortController | undefined
let skipHistoryForNextRequest = false
let textareaMeasure: HTMLTextAreaElement | undefined
const answerQueues = new Map<number, string>()
const answerQueueTimers = new Map<number, ReturnType<typeof window.setTimeout>>()
const pendingFollowups = new Map<number, string[]>()
const completedStreamMessageIds = new Set<number>()

const setMessageListRef = (element: Element | ComponentPublicInstance | null) => {
  const messageList = element instanceof HTMLElement ? element : undefined
  messageListRef.value = messageList
  if (messageList && isOpen.value && messages.value.length > 0) {
    void scrollToLatestMessage()
  }
}

const canSend = computed(() => Boolean(draft.value.trim() || draftImageUrl.value) && !isUploadingFile.value)
const latestFollowupMessageId = computed(() => {
  const latestMessage = messages.value.at(-1)
  return latestMessage?.role === 'assistant' && !latestMessage.isHumanService && latestMessage.followupQuestions?.length
    ? latestMessage.id
    : undefined
})
const sendButtonIcon = computed(() => (isReplying.value ? stopIcon : canSend.value ? sendIcon : idleSendIcon))
const sendButtonLabel = computed(() => (isReplying.value ? '停止生成' : canSend.value ? '发送消息' : '暂不可发送'))

const loadQuickQuestions = async () => {
  quickQuestions.value = []
  faqAnswers.value = new Map()

  try {
    const response = await getAiCustomerFaqs()
    const items = response.data.filter((item): item is AiCustomerFaq =>
      Boolean(item?.question?.trim() && item?.answer?.trim()),
    )

    if (items.length > 0) {
      quickQuestions.value = items.map((item) => item.question)
      faqAnswers.value = new Map(items.map((item) => [item.question, trimAnswerEnd(item.answer)]))
    }
  } catch (error) {
    console.warn('Failed to load AI customer service FAQs.', error)
  }
}

const loadLatestConversationId = async (shouldShowGuide: boolean) => {
  const savedConversationId = window.localStorage.getItem(AI_CUSTOMER_SERVICE_CONVERSATION_ID_KEY)?.trim()
  conversationId.value = savedConversationId || (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`)
  window.localStorage.setItem(AI_CUSTOMER_SERVICE_CONVERSATION_ID_KEY, conversationId.value)

  if (!shouldShowGuide && conversationId.value) {
    try {
      await loadChatRecords(conversationId.value)
    } finally {
      await nextTick()
      if (messageListRef.value) messageListRef.value.scrollTop = messageListRef.value.scrollHeight
      isHistoryLoading.value = false
    }
  } else {
    isHistoryLoading.value = false
  }
}

const loadChatRecords = async (currentConversationId: string) => {
  try {
    const response = await getAiCustomerChatRecords(currentConversationId, { limit: 100 })
    const records = [...response.data].sort((left, right) => {
      const leftTime = left.askedAt || left.createdAt
      const rightTime = right.askedAt || right.createdAt
      return leftTime - rightTime
    })

    const historyMessages = records.flatMap((record: AiCustomerChatRecord) => {
      const recordMessages: ChatMessage[] = []
      if (record.question || record.files?.length) {
        recordMessages.push({
          id: `${record.id}-question`,
          timestamp: record.askedAt || record.createdAt,
          isHistory: true,
          role: 'user',
          content: record.question || undefined,
          imageUrl: record.files?.[0]?.url,
        })
      }
      if (record.answer) {
        const answer = trimAnswerEnd(record.answer)
        recordMessages.push({ id: `${record.id}-answer`, role: 'assistant', content: answer, followupQuestions: record.followup?.questions?.slice(0, 3), isHumanService: answer.includes('人工客服'), timestamp: record.createdAt })
      }
      return recordMessages
    })

    const currentMessages = messages.value.filter((message) => !historyMessages.some((item) => item.id === message.id))
    messages.value = [...historyMessages, ...currentMessages]

    if (historyMessages.length > 0) {
      isGuideMode.value = false
      await nextTick()
      if (messageListRef.value) messageListRef.value.scrollTop = messageListRef.value.scrollHeight
      // 历史记录渲染及面板过渡动画可能会改变容器高度，分帧再次定位到底部
      requestAnimationFrame(() => {
        void scrollToLatestMessage()
        window.setTimeout(() => void scrollToLatestMessage(), 120)
      })
    }
  } catch (error) {
    console.warn('Failed to load AI customer service chat records.', error)
  }
}

const clearDraftImage = () => {
  if (draftImageUrl.value) {
    URL.revokeObjectURL(draftImageUrl.value)
    imageObjectUrls.delete(draftImageUrl.value)
    draftImageUrl.value = ''
  }
  draftFileId.value = ''
  isUploadingFile.value = false
  if (draft.value) window.requestAnimationFrame(resizeTextarea)
}

const clearReplyTimers = () => {
  if (faqReplyTimer) {
    window.clearTimeout(faqReplyTimer)
    faqReplyTimer = undefined
  }
  if (thinkingLabelTimer) {
    window.clearTimeout(thinkingLabelTimer)
    thinkingLabelTimer = undefined
  }
  answerQueueTimers.forEach((timer) => window.clearTimeout(timer))
  answerQueueTimers.clear()
  answerQueues.clear()
  pendingFollowups.clear()
  completedStreamMessageIds.clear()
}

const showSensitiveWordError = () => {
  if (sensitiveWordErrorTimer) window.clearTimeout(sensitiveWordErrorTimer)
  sensitiveWordError.value = true
  sensitiveWordErrorTimer = window.setTimeout(() => {
    sensitiveWordError.value = false
    sensitiveWordErrorTimer = undefined
  }, 3000)
}

const hideThinkingIndicator = () => {
  if (thinkingLabelTimer) {
    window.clearTimeout(thinkingLabelTimer)
    thinkingLabelTimer = undefined
  }
  thinkingLabelVisible.value = false
  isThinking.value = false
}

const stopReply = (markNextRequestWithoutHistory = false) => {
  if (markNextRequestWithoutHistory) {
    skipHistoryForNextRequest = true
  }
  const activeConversationId = isReplying.value ? conversationId.value : ''
  chatStreamController?.abort()
  chatStreamController = undefined
  clearReplyTimers()

  isReplying.value = false
  hideThinkingIndicator()

  if (activeConversationId) {
    void stopAiCustomerChat(activeConversationId).catch((error) => {
      console.warn('Failed to stop AI customer service response.', error)
    })
  }
}

const open = () => {
  stopReply()
  const lastOpenedAt = Number(window.localStorage.getItem(AI_CUSTOMER_SERVICE_LAST_OPENED_AT_KEY))
  const shouldShowGuide = !lastOpenedAt || Date.now() - lastOpenedAt >= AI_CUSTOMER_SERVICE_GUIDE_INTERVAL

  isGuideMode.value = shouldShowGuide
  isHistoryLoading.value = true
  messages.value = []
  draft.value = ''
  clearDraftImage()
  isComposerExpanded.value = false
  showScrollBottom.value = false
  autoScrollEnabled = true
  lastMessageListScrollTop = 0
  isOpen.value = true
  document.addEventListener('pointerdown', handleDocumentPointerDown)
  void loadQuickQuestions()
  void loadLatestConversationId(shouldShowGuide)
}

const close = () => {
  stopReply()
  isOpen.value = false
  document.removeEventListener('pointerdown', handleDocumentPointerDown)
}

const handleDocumentPointerDown = (event: PointerEvent) => {
  const target = event.target
  const isImagePreview = target instanceof Element && Boolean(target.closest('.preview-overlay'))
  if (target instanceof Node && !customerServiceRef.value?.contains(target) && !isImagePreview) close()
}

const activateConversation = () => {
  isGuideMode.value = false
  window.localStorage.setItem(AI_CUSTOMER_SERVICE_LAST_OPENED_AT_KEY, String(Date.now()))
}

const scrollToLatestMessage = async () => {
  if (!autoScrollEnabled) return
  await nextTick()
  if (scrollFrame !== undefined) {
    cancelAnimationFrame(scrollFrame)
  }
  scrollFrame = requestAnimationFrame(() => {
    const messageList = messageListRef.value
    if (messageList) {
      isProgrammaticScroll = true
      messageList.scrollTop = messageList.scrollHeight
      lastMessageListScrollTop = messageList.scrollTop
      requestAnimationFrame(() => {
        isProgrammaticScroll = false
      })
    }
    scrollFrame = undefined
  })
}

const handleContentScroll = (event: Event) => {
  const content = event.currentTarget as HTMLElement
  const distanceToBottom = content.scrollHeight - content.clientHeight - content.scrollTop
  const scrolledUp = content.scrollTop < lastMessageListScrollTop

  if (!isProgrammaticScroll) {
    if (scrolledUp) {
      autoScrollEnabled = false
    } else if (distanceToBottom <= 40) {
      autoScrollEnabled = true
    }
  }
  lastMessageListScrollTop = content.scrollTop
  showScrollBottom.value = distanceToBottom > 40
}

const scrollToBottom = () => {
  const content = messageListRef.value
  if (!content) return
  autoScrollEnabled = true
  isProgrammaticScroll = true
  content.scrollTo({ top: content.scrollHeight, behavior: 'smooth' })
  window.setTimeout(() => {
    lastMessageListScrollTop = content.scrollTop
    isProgrammaticScroll = false
  }, 300)
}

const measureTextareaHeight = (textarea: HTMLTextAreaElement, padding: string) => {
  if (!textareaMeasure) {
    textareaMeasure = document.createElement('textarea')
    textareaMeasure.setAttribute('aria-hidden', 'true')
    textareaMeasure.tabIndex = -1
    document.body.appendChild(textareaMeasure)
  }

  const computedStyle = window.getComputedStyle(textarea)
  const { width } = textarea.getBoundingClientRect()
  Object.assign(textareaMeasure.style, {
    position: 'fixed',
    top: '-9999px',
    left: '0',
    width: `${width}px`,
    height: '0px',
    minHeight: '0',
    maxHeight: 'none',
    padding,
    boxSizing: 'border-box',
    visibility: 'hidden',
    pointerEvents: 'none',
    overflow: 'hidden',
    resize: 'none',
    fontFamily: computedStyle.fontFamily,
    fontSize: computedStyle.fontSize,
    fontWeight: computedStyle.fontWeight,
    fontStyle: computedStyle.fontStyle,
    letterSpacing: computedStyle.letterSpacing,
    lineHeight: computedStyle.lineHeight,
    whiteSpace: 'pre-wrap',
    overflowWrap: computedStyle.overflowWrap,
    wordBreak: computedStyle.wordBreak,
  })
  textareaMeasure.value = textarea.value
  return textareaMeasure.scrollHeight
}

const resizeTextarea = () => {
  const textarea = textareaRef.value
  if (!textarea) return

  const lineHeight = Number.parseFloat(window.getComputedStyle(textarea).lineHeight) || 24
  const hasImage = Boolean(draftImageUrl.value)
  const collapsedPadding = '0 35px'
  const expandedPadding = '0'
  const imagePadding = '0 0 8px'
  const collapsedHeight = measureTextareaHeight(textarea, collapsedPadding)
  const expanded = Boolean(textarea.value) && collapsedHeight > lineHeight + 2
  isComposerExpanded.value = expanded

  const displayPadding = hasImage ? imagePadding : expanded ? expandedPadding : collapsedPadding
  const maxHeight = hasImage ? 145 : 211
  const displayHeight = expanded ? Math.min(measureTextareaHeight(textarea, displayPadding), maxHeight) : lineHeight
  textarea.style.height = `${displayHeight}px`
}

const handleTextareaEnter = (event: KeyboardEvent) => {
  if (event.isComposing || event.keyCode === 229) return
  event.preventDefault()
  submitDraft()
}

const openFilePicker = () => {
  fileInputRef.value?.click()
}

const handleFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  clearDraftImage()
  const imageUrl = URL.createObjectURL(file)
  imageObjectUrls.add(imageUrl)
  draftImageUrl.value = imageUrl
  input.value = ''
  isUploadingFile.value = true

  try {
    const response = await uploadAiCustomerFile(file)
    if (draftImageUrl.value === imageUrl) {
      draftFileId.value = response.data?.fileId ?? ''
    }
  } catch (error) {
    if (draftImageUrl.value === imageUrl) {
      clearDraftImage()
    }
    console.warn('Failed to upload AI customer service file.', error)
  } finally {
    if (draftImageUrl.value === imageUrl) {
      isUploadingFile.value = false
    }
  }
}

const getReply = (question: string) => {
  return faqAnswers.value.get(question) ?? ''
}

const trimAnswerEnd = (content: string) => content.replace(/(?:\r?\n)+\s*$/, '')

const messageTimeLabels = computed(() => {
  const labels = new Map<number | string, 'time' | 'datetime'>()
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const fourHours = 4 * 60 * 60 * 1000
  let latestMessageOverall: ChatMessage | undefined
  let firstToday: ChatMessage | undefined
  const historicalDays = new Map<number, ChatMessage>()
  const historicalBuckets = new Map<number, ChatMessage>()

  messages.value.forEach((message) => {
    // 时间标签始终显示在用户问题上方，不显示在客服回答上方
    if (message.role !== 'user' || !message.isHistory) return
    if (!message.timestamp) return
    if (!latestMessageOverall || message.timestamp > (latestMessageOverall.timestamp || 0)) latestMessageOverall = message
    if (message.timestamp >= todayStart) {
      if (!firstToday || message.timestamp < (firstToday.timestamp || 0)) firstToday = message
      return
    }
    const date = new Date(message.timestamp)
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
    const firstMessage = historicalDays.get(dayStart)
    if (!firstMessage || message.timestamp < (firstMessage.timestamp || 0)) historicalDays.set(dayStart, message)

    const bucket = Math.floor((todayStart - 1 - message.timestamp) / fourHours)
    const latestMessage = historicalBuckets.get(bucket)
    if (!latestMessage || message.timestamp > (latestMessage.timestamp || 0)) historicalBuckets.set(bucket, message)
  })

  if (firstToday) labels.set(firstToday.id, 'datetime')
  historicalBuckets.forEach((message) => labels.set(message.id, 'datetime'))
  historicalDays.forEach((message) => labels.set(message.id, 'datetime'))
  if (latestMessageOverall && (latestMessageOverall.timestamp ?? 0) >= todayStart) {
    labels.set(latestMessageOverall.id, 'time')
  }
  return labels
})
const formatMessageTime = (timestamp: number, includeDate = false) => {
  const date = new Date(timestamp)
  const pad = (value: number) => String(value).padStart(2, '0')
  if (includeDate) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
  }
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const handleMarkdownLinkClick = (event: MouseEvent) => {
  const link = event.composedPath().find((node): node is HTMLAnchorElement =>
    node instanceof Element && node.tagName.toLowerCase() === 'a',
  ) as HTMLAnchorElement | undefined
  if (!link?.href) {
    return
  }

  event.preventDefault()
  event.stopPropagation()
  const isMallLink = link.getAttribute('href') === 'weixin://shopping/mall/index'
  if (isMallLink) {
    window.location.assign('https://mixedspace.njyjxr.com/#/mall')
    return
  }

  window.open(link.href, '_blank', 'noopener,noreferrer')
}

const saveFaqQuestion = async (question: string, answer: string) => {
  if (!conversationId.value || !question || !answer) return

  try {
    await recordAiCustomerFaq(conversationId.value, { platform: 3, question, answer })
  } catch (error) {
    console.warn('Failed to save AI customer service FAQ record.', error)
  }
}

const getChatHistory = (excludeMessageId?: number | string): AiCustomerChatHistoryItem[] => {
  return messages.value
    .filter((message) => message.id !== excludeMessageId)
    .flatMap((message) => (message.content ? [{ role: message.role, content: message.content }] : []))
    .slice(-3)
}

const normalizeAnswerLineBreaks = (content: string) => content.replace(/\r\n?/g, '\n').replace(/\n{2,}/g, '\n')

const appendStreamContent = (messageId: number, content: string) => {
  const message = messages.value.find((item) => item.id === messageId)
  const nextContent = message?.content?.endsWith('\n') ? content.replace(/^\r?\n+/, '') : content
  answerQueues.set(messageId, normalizeAnswerLineBreaks(`${answerQueues.get(messageId) ?? ''}${nextContent}`))
  if (answerQueueTimers.has(messageId)) return
  const appendNext = () => {
    const queued = answerQueues.get(messageId) ?? ''
    if (!queued) {
      const message = getOrCreateAssistantMessage(messageId)
      const followups = pendingFollowups.get(messageId)
      if (followups?.length && !message.isHumanService) {
        message.followupQuestions = followups
      }
      pendingFollowups.delete(messageId)
      answerQueueTimers.delete(messageId)
      finishAiReplyIfRendered(messageId)
      return
    }
    const message = getOrCreateAssistantMessage(messageId)
    message.content = `${message.content ?? ''}${queued[0]}`
    answerQueues.set(messageId, queued.slice(1))
    answerQueueTimers.set(messageId, window.setTimeout(appendNext, 40))
    void scrollToLatestMessage()
  }
  appendNext()
}

const finishAiReplyIfRendered = (messageId: number) => {
  if (
    !completedStreamMessageIds.has(messageId) ||
    answerQueueTimers.has(messageId) ||
    (answerQueues.get(messageId) ?? '').length > 0
  ) {
    return
  }

  const message = messages.value.find((item) => item.id === messageId)
  if (message?.content) {
    message.content = trimAnswerEnd(message.content)
    if (message.content.includes('人工客服')) {
      message.isHumanService = true
      message.followupQuestions = undefined
    }
  }
  completedStreamMessageIds.delete(messageId)
  isReplying.value = false
  hideThinkingIndicator()
}

const getOrCreateAssistantMessage = (messageId: number) => {
  let message = messages.value.find((item) => item.id === messageId)
  if (!message) {
    message = { id: messageId, role: 'assistant' }
    messages.value.push(message)
  }
  return message
}

const handleAiStreamEvent = (messageId: number, event: AiCustomerChatStreamEvent) => {
  if (event.type === 'done') {
    completedStreamMessageIds.add(messageId)
    finishAiReplyIfRendered(messageId)
    return
  }

  if (event.type === 'classify' || event.type === 'retrieval' || event.type === 'think' || event.type === 'warning') {
    return
  }

  const message = getOrCreateAssistantMessage(messageId)
  switch (event.type) {
    case 'answer':
      if (event.content) {
        hideThinkingIndicator()
        appendStreamContent(messageId, event.content)
      }
      return
    case 'correction':
      if (event.content) {
        const correction = normalizeAnswerLineBreaks(trimAnswerEnd(event.content))
        const renderedContent = message.content ?? ''
        const timer = answerQueueTimers.get(messageId)
        if (timer) window.clearTimeout(timer)
        answerQueueTimers.delete(messageId)
        answerQueues.delete(messageId)
        if (correction.startsWith(renderedContent)) {
          appendStreamContent(messageId, correction.slice(renderedContent.length))
        } else {
          appendStreamContent(messageId, correction)
        }
      }
      break
    case 'followup':
      if (event.questions?.length && !message.isHumanService) {
        if ((answerQueues.get(messageId) ?? '').length > 0) pendingFollowups.set(messageId, event.questions)
        else message.followupQuestions = event.questions
      }
      break
  }

  void scrollToLatestMessage()
}

const requestAiAnswer = async (query: string, fileId: string, history: AiCustomerChatHistoryItem[]) => {
  if (!conversationId.value) return

  const controller = new AbortController()
  chatStreamController = controller
  const assistantMessageId = Date.now() + 1
  let requestFailed = false

  try {
    const request = { platform: 3, query, fileIds: fileId ? [fileId] : undefined, history }
    await streamAiCustomerChat(
      conversationId.value,
      request,
      (event) => {
        if (!controller.signal.aborted) {
          handleAiStreamEvent(assistantMessageId, event)
        }
      },
      controller.signal,
    )
  } catch (error) {
    if (!controller.signal.aborted) {
      requestFailed = true
      console.warn('Failed to request AI customer service response.', error)
      const userMessage = messages.value.slice().reverse().find((message) => message.role === 'user' && !message.retryable)
      if (userMessage) userMessage.retryable = true
    }
  } finally {
    if (chatStreamController === controller) {
      chatStreamController = undefined
      completedStreamMessageIds.add(assistantMessageId)
      if (!controller.signal.aborted) {
        const assistantMessage = messages.value.find((message) => message.id === assistantMessageId)
        if (!assistantMessage?.content) {
          const userMessage = messages.value.slice().reverse().find((message) => message.role === 'user' && !message.retryable)
          if (userMessage) userMessage.retryable = true
          if (assistantMessage) {
            const messageIndex = messages.value.findIndex((item) => item.id === assistantMessageId)
            if (messageIndex >= 0) messages.value.splice(messageIndex, 1)
          } else if (!requestFailed) {
            // 空响应只显示提问下方的重试状态，不追加错误客服消息。
          }
        }
      }
      finishAiReplyIfRendered(assistantMessageId)
      await scrollToLatestMessage()
    }
  }
}

const streamFaqAnswer = (answer: string) => {
  const assistantMessage: ChatMessage = { id: Date.now() + 1, role: 'assistant', content: '' }
  const characters = Array.from(normalizeAnswerLineBreaks(answer))
  let cursor = 0
  assistantMessage.timestamp = Date.now()
  messages.value.push(assistantMessage)

  const appendNextCharacter = async () => {
    if (!isReplying.value || cursor >= characters.length) {
      const finalContent = trimAnswerEnd(assistantMessage.content ?? '')
      assistantMessage.content = finalContent
      if (finalContent.includes('人工客服')) {
        assistantMessage.isHumanService = true
        assistantMessage.followupQuestions = undefined
      }
      messages.value = messages.value.map((message) =>
        message.id === assistantMessage.id ? { ...message, content: finalContent } : message,
      )
      isReplying.value = false
      faqReplyTimer = undefined
      void scrollToLatestMessage()
      return
    }

    if (!assistantMessage.content) hideThinkingIndicator()
    const character = characters[cursor] ?? ''
    const nextContent = `${assistantMessage.content ?? ''}${character}`
    assistantMessage.content = nextContent
    messages.value = messages.value.map((message) =>
      message.id === assistantMessage.id ? { ...message, content: nextContent } : message,
    )
    cursor += 1
    await nextTick()
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
    if (cursor % 6 === 0 || cursor >= characters.length) {
      void scrollToLatestMessage()
    }

    const lastCharacter = characters[cursor - 1] ?? ''
    const delay = /[。！？；：.!?;:]/.test(lastCharacter) ? 210 : /[，、,]/.test(lastCharacter) ? 125 : 65
    faqReplyTimer = window.setTimeout(appendNextCharacter, 40)
  }

  faqReplyTimer = window.setTimeout(appendNextCharacter, 180)
}

const sendMessage = async ({ content = '', imageUrl = '', fileId = '' }: SendMessagePayload, existingMessage?: ChatMessage) => {
  const question = content.trim()
  if ((!question && !imageUrl) || isReplying.value) {
    return
  }

  if (messages.value.length === 0 && conversationId.value) {
    await loadChatRecords(conversationId.value)
    isHistoryLoading.value = false
  }
  activateConversation()
  isHistoryLoading.value = false
  messages.value.forEach((message) => {
    message.followupQuestions = undefined
  })
  const history = skipHistoryForNextRequest ? [] : getChatHistory(existingMessage?.id)
  skipHistoryForNextRequest = false
  const faqAnswer = question ? getReply(question) : ''
  if (faqAnswer) {
    void saveFaqQuestion(question, faqAnswer)
  }
  if (existingMessage) {
    existingMessage.retryable = false
  } else {
    messages.value.push({
      id: Date.now(),
      role: 'user',
      timestamp: Date.now(),
      content: question || undefined,
      imageUrl: imageUrl || undefined,
      fileId: fileId || undefined,
    })
  }
  clearReplyTimers()
  thinkingLabelVisible.value = false
  isThinking.value = true
  isReplying.value = true
  thinkingLabelTimer = window.setTimeout(() => {
    thinkingLabelVisible.value = true
    thinkingLabelTimer = undefined
    void scrollToLatestMessage()
  }, 520)
  await scrollToLatestMessage()

  if (faqAnswer) {
    streamFaqAnswer(faqAnswer)
  } else {
    void requestAiAnswer(question, fileId, history)
  }
}

const submitDraft = async () => {
  if (!canSend.value || isReplying.value) {
    return
  }

  sensitiveWordError.value = false
  if (draft.value.trim()) {
    try {
      const response = await fetch(`${aiBaseUrl}/sensitive/sensitiveWord/contains?text=${encodeURIComponent(draft.value.trim())}`, { method: 'POST' })
      const result = await response.json()
      if (result?.data === true || result?.data?.data === true) {
        showSensitiveWordError()
        return
      }
    } catch (error) {
      showSensitiveWordError()
      return
    }
  }
  const imageUrl = draftImageUrl.value
  const fileId = draftFileId.value
  const content = draft.value

  await sendMessage({ content, imageUrl, fileId })
  draft.value = ''
  draftImageUrl.value = ''
  draftFileId.value = ''
  await nextTick()
  resizeTextarea()
}

const retryMessage = (message: ChatMessage) => {
  if (isReplying.value) return
  message.retryable = false
  void sendMessage({ content: message.content, imageUrl: message.imageUrl, fileId: message.fileId }, message)
}

onBeforeUnmount(() => {
  if (sensitiveWordErrorTimer) window.clearTimeout(sensitiveWordErrorTimer)
  document.removeEventListener('pointerdown', handleDocumentPointerDown)
  stopReply()
  textareaMeasure?.remove()
  textareaMeasure = undefined
  imageObjectUrls.forEach((imageUrl) => URL.revokeObjectURL(imageUrl))
})
</script>

<style scoped lang="less">
.ai-customer-service {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 30000;
}

.ai-customer-service:not(.ai-customer-service--open) {
  top: 33.3333%;
  bottom: auto;
}

.ai-customer-service,
.ai-customer-service :deep(*) {
  font-family: 'Noto Sans SC', 'Source Han Sans SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC',
    'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
}

.service-launcher {
  position: fixed;
  top: 33.3333%;
  right: 16px;
  bottom: auto;
  display: grid;
  width: 72px;
  height: 72px;
  padding: 0;
  cursor: pointer;
  border: 0;
  border-radius: 50%;
  background: transparent;
  place-items: center;
  transform: translateY(-50%);
  transition:
    filter 160ms ease,
    transform 160ms ease;

  img {
    display: block;
    display: block;
    width: 72px;
    height: 72px;
  }

  &:hover {
    filter: drop-shadow(0 5px 9px rgba(46, 118, 255, 0.28));
    transform: translateY(calc(-50% - 2px));
  }

  &:focus-visible {
    outline: 2px solid #2e76ff;
    outline-offset: 3px;
  }
}

.service-panel {
  position: relative;
  display: flex;
  width: min(414px, calc(100vw - 48px));
  height: min(68svh, calc(100svh - 48px));
  flex-direction: column;
  overflow: hidden;
  border-radius: 27px;
  background: #ffffff;
  box-shadow: 0 -1px 20px 8px rgba(201, 191, 191, 0.25);
}

.service-panel__topbar {
  position: relative;
  flex: 0 0 auto;
  overflow: hidden;
}

.ai-service-header-enter-active,
.ai-service-header-leave-active {
  transition:
    opacity 240ms ease,
    transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
}

.ai-service-header-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.ai-service-header-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

.guide-header {
  position: relative;
  display: flex;
  height: auto;
  padding: 40px 20px 0px;
  box-sizing: border-box;
  align-items: flex-start;
  justify-content: space-between;
}

.guide-profile {
  display: flex;
  align-items: center;
  gap: 15px;

  .guide-avatar {
    width: 104px;
    height: 104px;
    object-fit: contain;
  }

  .guide-title,
  span {
    display: block;
    margin: 0;
  }

  .guide-title {
    width: 171px;
    height: 30px;
    object-fit: contain;
    object-position: left center;
  }

  span {
    margin-top: 8px;
    color: #4e90ff;
    font-size: 16px;
    line-height: 20px;
    text-align: left;
  }
}

.guide-close-icon {
  position: absolute;
  top: 14px;
  right: 20px;
  width: 24px;
  height: 24px;
  padding: 4px;
  box-sizing: content-box;
  cursor: pointer;
  object-fit: contain;
  transition: opacity 160ms ease;
  border-radius: 10px;

  &:hover {
    opacity: 0.72;
    background: #f3f3f3;
  }

  &:focus-visible {
    outline: 2px solid #2e76ff;
    outline-offset: 2px;
    background: #f3f3f3;
  }
}

.service-panel__header {
  position: relative;
  display: grid;
  height: 54px;
  padding: 10px 20px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  grid-template-columns: 36px 1fr 28px;
  align-items: center;

   h2 {
    margin: 0;
    color: #202020;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    text-align: left;
    margin-left: 8px;
  }
}

.service-panel__avatar {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.composer-icon-button,
.send-button {
  display: grid;
  padding: 0;
  cursor: pointer;
  border: 0;
  background: transparent;
  place-items: center;
}

.close-icon {
  width: 24px;
  height: 24px;
  padding: 4px;
  box-sizing: content-box;
  cursor: pointer;
  object-fit: contain;
  transition: opacity 160ms ease;
  border-radius: 10px;

  &:hover {
    opacity: 0.72;
    background: #f3f3f3;
  }

  &:focus-visible {
    outline: 2px solid #2e76ff;
    outline-offset: 2px;
    background: #f3f3f3;
  }
}

.service-panel__content {
  min-height: 0;
  flex: 1;
  margin-bottom: 20px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 18px 20px 16px;
  scrollbar-width: thin;
  transition:
    padding-top 320ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}


.service-panel__content--guide {
  padding-top: 18px;
}

.service-panel__content--guide .quick-question {
  animation: quick-question-in 260ms both;

  &:nth-child(2) {
    animation-delay: 45ms;
  }

  &:nth-child(3) {
    animation-delay: 90ms;
  }

  &:nth-child(4) {
    animation-delay: 135ms;
  }
}

.service-panel__prompt {
  margin: 0;
  color: #7f7f7f;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
}

.quick-questions {
  display: flex;
  margin-top: 18px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.quick-question {
  display: inline-flex;
  max-width: 100%;
  padding: 6px 12px 6px 12px;
  cursor: pointer;
  border: 1px solid #d3e1ee;
  border-radius: 62px;
  background: #ffffff;
  color: #303030;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  align-items: center;
  gap: 4px;
  transition:
    background-color 160ms ease,
    border-color 160ms ease,
    color 160ms ease;

  img {
    width: 28px;
    height: 28px;
    flex: 0 0 auto;
    object-fit: contain;
     position: relative;
    top: 1px;
  }

  span {
    min-width: 0;
  }

  &:hover {
    border-color: #9ec1fa;
    background: #f7faff;
    color: #2465d1;
  }

  &:focus-visible {
    outline: 2px solid #2e76ff;
    outline-offset: 2px;
  }
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.message-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &--user {
    align-items: flex-end;
  }
}

.message-time {
  width: 100%;
  margin: 0px 0 16px;
  color: #1e1e1e;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
}

.message-bubble {
  display: block;
  width: fit-content;
  padding: 10px 16px;
  box-sizing: border-box;
  margin: 0;
  background: #f5f5f5;
  color: #1e1e1e;
  font-size: 16px;
  font-weight: 500;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;
  box-shadow:
    inset 0 1px 1px 0 #ffffff,
    inset 0 -1px 1px 0 rgba(255, 255, 255, 0.62);
  border-radius: 4px 22px 22px;

  .message-row--user & {
    background: #539dff;
    color: #ffffff;
    box-shadow: none;
    border-radius: 22px 4px 22px 22px;
  }
}

.message-row--assistant .message-bubble {
  line-height: 28px;
  white-space: normal;
}

.message-bubble :deep(.streaming-tail) {
  color: transparent;
  background: linear-gradient(90deg, rgba(30, 30, 30, 0.86) 0%, rgba(30, 30, 30, 0.8) 55%, rgba(30, 30, 30, 0.62) 100%);
  background-clip: text;
  -webkit-background-clip: text;
}

.message-bubble--error {
  color: #ba1e1e;
  font-size: 14px;
  font-weight: 400;
}

.message-retry-area {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.message-retry-status {
  color: #939aad;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.message-retry-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 81px;
  height: 36px;
  margin-top: 0;
  padding: 0;
  border: 0;
  border-radius: 50px;
  background: #f5f9ff;
  box-shadow: inset 0 1px 1px 0 #ffffff, inset 0 -1px 1px 0 rgba(255, 255, 255, 0.62);
  color: #2465d1;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.message-retry-button img {
  width: 16px;
  height: 16px;
  margin-right: 4px;
  object-fit: contain;
}

.message-bubble :deep(p) {
  margin: 0;
}

.message-bubble :deep(p + p),
.message-bubble :deep(ul),
.message-bubble :deep(ol),
.message-bubble :deep(blockquote),
.message-bubble :deep(pre) {
  margin: 0;
}

.message-bubble :deep(p + p) {
  margin-top: 16px;
}

.message-bubble :deep(ul),
.message-bubble :deep(ol) {
  padding-left: 20px;
}

.message-bubble :deep(pre) {
  max-width: 100%;
  overflow-x: auto;
  padding: 8px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.06);
  white-space: pre;
}

.message-bubble :deep(code) {
  padding: 1px 4px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
  font-family: Consolas, monospace;
}

.message-bubble :deep(a) {
  color: #2e76ff;
  text-decoration: none;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.message-bubble :deep(a.mall-link)::after {
  content: ' →';
  color: #2e76ff;
  font-weight: 600;
}

.message-image {
  display: block;
  max-width: 180px;
  max-height: 180px;
  border-radius: 8px;
  object-fit: cover;
  cursor: zoom-in;
}

.message-image + .message-bubble {
  margin-top: 8px;
}

.human-service-card {
  display: flex;
  width: 192px;
  margin: 8px 0;
  padding: 16px;
  box-sizing: border-box;
  flex-direction: column;
  align-items: center;
  border-radius: 4px 22px 22px;
  background: #f5f5f5;
  box-shadow:
    inset 0 1px 1px 0 #ffffff,
    inset 0 -1px 1px 0 rgba(255, 255, 255, 0.62);

  span {
    width: 100%;
    color: #1e1e1e;
    font-size: 14px;
    font-weight: 500;
    text-align: center;
  }

  span:first-child {
    font-size: 16px;
  }

  img {
    display: block;
    width: 152px;
    height: 152px;
    margin-top: 10px;
    object-fit: cover;
    cursor: zoom-in;
  }
}

.message-followups {
  display: flex;
  max-width: 100%;
  margin-top: 10px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  button {
    max-width: 100%;
    padding: 10px 16px;
    cursor: pointer;
    border: 1px solid #d3e1ee;
    border-radius: 20px;
    background: #ffffff;
    font: inherit;
    line-height: 18px;
    text-align: left;
    font-weight: 500;
    font-size: 16px;
    color: #1E1E1E;
    transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease;

    &:hover,
    &:active {
      border-color: #9ec1fa;
      background: #f7faff;
      color: #2465d1;
    }

    &:focus-visible {
      outline: 2px solid #2e76ff;
      outline-offset: 2px;
    }
  }
}

.thinking-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 8px 12px;
  border-radius: 18px;
  background: #f5f7fb;
  animation: thinking-pulse 1.15s ease-in-out infinite alternate;

  .thinking-dots {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 3px;
  }

  .thinking-dots i {
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    animation: thinking-dot 1.2s ease-in-out infinite;

    &:nth-child(1) {
      background: rgba(147, 154, 173, 0.3);
      animation-delay: 0ms;
    }

    &:nth-child(2) {
      background: rgba(147, 154, 173, 0.6);
      animation-delay: 160ms;
    }

    &:nth-child(3) {
      background: #939aad;
      animation-delay: 320ms;
    }
  }

  .thinking-label {
    display: block;
    background: linear-gradient(90deg, rgba(147, 154, 173, 0.3) 0%, rgba(147, 154, 173, 0.6) 37%, rgba(147, 154, 173, 0.8) 63%, #939aad 100%);
    background-clip: text;
    color: transparent;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    text-align: left;
    text-transform: none;
    -webkit-background-clip: text;
    animation: thinking-label-in 180ms ease-out both;
  }
}

.service-panel__composer-area {
  position: relative;
  flex: 0 0 auto;
}

.sensitive-word-error {
  position: absolute;
  bottom: calc(100% + 16px);
  left: 50%;
  display: flex;
  width: 328px;
  height: 43px;
  box-sizing: border-box;
  transform: translateX(-50%);
  border-radius: 26px;
  background: linear-gradient(180deg, #fefbf6 0%, #fef7ed 100%);
  align-items: center;
  color: #797979;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  justify-content: center;
}

.sensitive-word-error img { width: 32px; height: 32px; object-fit: contain; }

.scroll-bottom-slot {
  position: absolute;
  right: 20px;
  bottom: calc(100% + 12px);
  z-index: 1;
}

.scroll-bottom-button {
  position: relative;
  display: grid;
  width: 36px;
  height: 36px;
  padding: 0;
  cursor: pointer;
  border: 0;
  background: transparent;
  place-items: center;
  transition:
    opacity 160ms ease,
    transform 160ms ease;

  .scroll-bottom-icon {
    position: relative;
    z-index: 1;
    width: 36px;
    height: 36px;
    object-fit: contain;
  }

  &:hover {
    transform: translateY(-2px);
  }
}

.scroll-bottom-spinner {
  position: absolute;
  top: -0px;
  left: 2px;
  width: 32px;
  height: 32px;
  z-index: 0;
  object-fit: contain;
  animation: scroll-bottom-button-spin 900ms linear infinite;
}

.message-composer {
  position: relative;
  display: block;
  min-height: 44px;
  max-height: 265px;
  margin: 0 20px 20px;
  padding: 12px 16px;
  box-sizing: border-box;
  border-radius: 999px;
  background: #f5f5f5;
  box-shadow:
    inset 0 1px 1px 0 rgba(219, 219, 219, 0.25),
    inset 0 -1px 1px 0 rgba(219, 219, 219, 0.25);
  transition:
    min-height 220ms ease,
    padding 220ms ease,
    border-radius 220ms ease;

  &--expanded {
    border-radius: 20px;
    padding-bottom: 42px;
  }

  &--with-image {
    border-radius: 20px;
    transition: none;
  }

  &:not(.message-composer--expanded):not(.message-composer--with-image) {
    min-height: 50px;
    padding: 12px 16px;

    .composer-icon-button {
      bottom: 9px;
    }

    .send-button {
      bottom: 7px;
    }
  }
}

.file-input {
  display: none;
}

.composer-icon-button {
  position: absolute;
  bottom: 6px;
  left: 12px;
  display: grid;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  flex: 0 0 auto;
  margin-left: 0;
  cursor: pointer;
  place-items: center;

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }

  &:hover,
  &:focus-visible {
    background: #ffffff;
  }
}

.message-composer__field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.draft-preview {
  position: relative;
  width: 78px;
  height: 78px;
  margin: 6px 0 2px 0px;
  flex: 0 0 auto;
}

.draft-image {
  display: block;
  width: 78px;
  height: 78px;
  border-radius: 4px;
  object-fit: cover;
}

.draft-image__remove {
  position: absolute;
  top: -8px;
  right: -8px;
  display: grid;
  width: 20px;
  height: 20px;
  padding: 0;
  cursor: pointer;
  border: 0;
  background: transparent;
  place-items: center;

  img {
    display: block;
    width: 20px;
    height: 20px;
    opacity: 1;
    object-fit: contain;
  }
}

.message-composer textarea {
  width: 100%;
  min-width: 0;
  max-height: 211px;
  padding: 0 35px;
  box-sizing: border-box;
  resize: none;
  border: 0;
  outline: 0;
  background: transparent;
  color: #303030;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  overflow-y: auto;
  transition:
    height 220ms cubic-bezier(0.22, 1, 0.36, 1),
    padding 220ms ease;

  &::placeholder {
    color: #aab4c5;
  }

  &.message-composer__textarea--expanded {
    padding: 0;
  }

  &.message-composer__textarea--with-image {
    max-height: 145px;
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 8px;
    overflow-y: auto;
    transition: none;
  }
}

.send-button {
  position: absolute;
  right: 12px;
  bottom: 6px;
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  transition: transform 160ms ease;

  img {
    position: relative;
    z-index: 1;
    width: 36px;
    height: 36px;
    object-fit: contain;
  }

  img.send-button__icon {
    width: 46px !important;
    height: 46px !important;
    transform: translateY(-6px);
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
  }
}

.ai-service-panel-enter-active,
.ai-service-panel-leave-active {
  transform-origin: right bottom;
  transition:
    opacity 220ms ease,
    transform 300ms cubic-bezier(0.22, 1, 0.36, 1);
}

.ai-service-panel-enter-from,
.ai-service-panel-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
}

@keyframes quick-question-in {
  from {
    opacity: 0;
    transform: translateY(5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes thinking-pulse {
  from {
    opacity: 0.55;
  }

  to {
    opacity: 1;
  }
}

@keyframes thinking-dot {
  0%,
  60%,
  100% {
    opacity: 0.45;
    transform: translateY(0);
  }

  30% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

@keyframes thinking-label-in {
  from {
    opacity: 0;
    transform: translateX(-4px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes scroll-bottom-button-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 600px) {
  .ai-customer-service {
    right: 24px;
    bottom: 24px;
  }

  .service-panel {
    width: calc(100vw - 48px);
    height: min(683px, calc(100svh - 48px));
  }

  .guide-header {
    padding: 30px 20px 6px;
  }

  .guide-profile {
    gap: 10px;

    .guide-avatar {
      width: 92px;
      height: 92px;
    }

    .guide-title {
      width: 143px;
      height: 25px;
    }
  }
}
</style>
